import { useEffect, useState, type ReactNode } from 'react';
import {
  closestCorners,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Eye, EyeOff, GripVertical } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CardId, Column } from '@/lib/dashboard-layout';

function useArrangeSensors() {
  return useSensors(
    // A few pixels of travel before a drag starts, so a tap on the hide button
    // or a scroll on a phone isn't read as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

/**
 * The frame a tile or card wears while the dashboard is being arranged: a
 * handle to drag by, its name, and a button to hide it. The content beneath
 * is inert so arranging never follows a link by accident.
 */
function ArrangeFrame({
  id,
  label,
  onHide,
  compact = false,
  children,
}: {
  id: string;
  label: string;
  onHide: () => void;
  compact?: boolean;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      // Translate only: tiles and cards differ in size, and scaling to match a
      // neighbour mid-drag would squash the content.
      style={{ transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, transition }}
      className={cn(
        'relative rounded-xl border border-dashed border-border bg-muted/20',
        isDragging && 'z-10 border-primary/60 opacity-90 shadow-lg',
      )}
    >
      <div className={cn('flex items-center gap-1.5', compact ? 'px-2 pt-1.5' : 'px-3 pt-2')}>
        <button
          ref={setActivatorNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Move ${label}`}
          className="flex size-6 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="size-4" />
        </button>
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">{label}</span>
        <button
          type="button"
          onClick={onHide}
          aria-label={`Hide ${label}`}
          title="Hide"
          className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <EyeOff className="size-3.5" />
        </button>
      </div>
      <div className={cn('pointer-events-none select-none', compact ? 'p-1.5' : 'p-2')} aria-hidden>
        {children}
      </div>
    </div>
  );
}

/** The stat tiles: dragged into any order along their row. */
export function ArrangeableTiles<T extends string>({
  ids,
  labelOf,
  render,
  onReorder,
  onHide,
  className,
}: {
  ids: T[];
  labelOf: (id: T) => string;
  render: (id: T) => ReactNode;
  onReorder: (ids: T[]) => void;
  onHide: (id: T) => void;
  className: string;
}) {
  const sensors = useArrangeSensors();

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(ids, ids.indexOf(active.id as T), ids.indexOf(over.id as T)));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className={className}>
          {ids.map((id) => (
            <ArrangeFrame key={id} id={id} label={labelOf(id)} onHide={() => onHide(id)} compact>
              {render(id)}
            </ArrangeFrame>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

type Columns = Record<Column, CardId[]>;

function columnOf(columns: Columns, id: string): Column | null {
  if (id === 'main' || id === 'side') return id;
  if (columns.main.includes(id as CardId)) return 'main';
  if (columns.side.includes(id as CardId)) return 'side';
  return null;
}

/**
 * The two card columns, with cards dragged within a column or across to the
 * other. The move is held locally while dragging and handed back once, on
 * drop, so hovering never saves.
 */
export function ArrangeableColumns({
  main,
  side,
  labelOf,
  render,
  onChange,
  onHide,
}: {
  main: CardId[];
  side: CardId[];
  labelOf: (id: CardId) => string;
  render: (id: CardId) => ReactNode;
  onChange: (columns: Columns) => void;
  onHide: (id: CardId) => void;
}) {
  const sensors = useArrangeSensors();
  const [columns, setColumns] = useState<Columns>({ main, side });

  // Follow the saved layout whenever it changes from outside a drag (a hide,
  // a reset, or the save landing).
  useEffect(() => setColumns({ main, side }), [main, side]);

  function onDragOver({ active, over }: DragOverEvent) {
    if (!over) return;
    const from = columnOf(columns, String(active.id));
    const to = columnOf(columns, String(over.id));
    if (!from || !to || from === to) return;

    setColumns((current) => {
      const moving = active.id as CardId;
      const target = current[to];
      const overIndex = target.indexOf(over.id as CardId);
      const at = overIndex === -1 ? target.length : overIndex;
      return {
        ...current,
        [from]: current[from].filter((id) => id !== moving),
        [to]: [...target.slice(0, at), moving, ...target.slice(at)],
      };
    });
  }

  function onDragEnd({ active, over }: DragEndEvent) {
    let next = columns;
    const column = columnOf(columns, String(active.id));
    if (over && column && columnOf(columns, String(over.id)) === column && active.id !== over.id) {
      const list = columns[column];
      const overIndex = list.indexOf(over.id as CardId);
      if (overIndex !== -1) {
        next = { ...columns, [column]: arrayMove(list, list.indexOf(active.id as CardId), overIndex) };
        setColumns(next);
      }
    }
    onChange(next);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragOver={onDragOver} onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        {(['main', 'side'] as const).map((column) => (
          <DropColumn key={column} id={column} className={column === 'main' ? 'xl:col-span-2' : undefined}>
            <SortableContext items={columns[column]} strategy={verticalListSortingStrategy}>
              {columns[column].map((id) => (
                <ArrangeFrame key={id} id={id} label={labelOf(id)} onHide={() => onHide(id)}>
                  {render(id)}
                </ArrangeFrame>
              ))}
            </SortableContext>
          </DropColumn>
        ))}
      </div>
    </DndContext>
  );
}

/** A column that takes a dropped card even when it's empty. */
function DropColumn({ id, className, children }: { id: Column; className?: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-32 space-y-5 rounded-xl border border-dashed border-transparent p-1 transition-colors',
        isOver && 'border-primary/40 bg-primary/5',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Everything hidden, each with a way back. Only shown while arranging. */
export function HiddenTray({ items }: { items: Array<{ key: string; label: string; onShow: () => void }> }) {
  return (
    <div className="rounded-xl border border-dashed border-border/70 bg-muted/30 p-4">
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Hidden</div>
      {items.length === 0 ? (
        <p className="mt-1.5 text-sm text-muted-foreground">Nothing is hidden. Use the eye on any tile or card to hide it.</p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={item.onShow}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-card px-2.5 py-1.5 text-sm hover:border-primary/40"
            >
              <Eye className="size-3.5 text-muted-foreground" /> {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
